import React, { useEffect, useState, useMemo, useCallback } from "react";
import {
    Box, CssBaseline, Typography, Avatar, IconButton,
    Stack, Button, Paper, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Chip, TextField, InputAdornment, Menu, MenuItem,
    TablePagination, Breadcrumbs, Link, CircularProgress,
    Dialog, DialogTitle, Snackbar, Alert, Tabs, Tab, Autocomplete
} from '@mui/material';
import {
    Search, MoreVert, Edit, WarningAmber, FilterList
} from '@mui/icons-material';
import { useNavigate } from "react-router-dom";
import Header from "../../component/header/Header";

const colors = {
    pageBg: '#F8F9FA',
    sidebarBg: '#F1F2F6',
    filterBg: "#FFF9E6",
    accent: '#FFC107',
    tableHeader: '#FDF7E2',
    textMain: '#5D4037',
    accentDark: '#FFA000',
    white: '#FFFFFF'
};

export default function ExamMissingStudent() {
    const navigate = useNavigate();

    // --- Data States ---
    const [missingStudentList, setMissingStudentList] = useState([]);
    const [studentList, setStudentList] = useState([]);
    const [studentInfo, setStudentInfo] = useState(null); // Changed to object for Autocomplete
    
    // --- UI/Status States ---
    const [loading, setLoading] = useState(false);
    const [loadingInfo, setLoadingInfo] = useState(false);
    const [loadingSubmit, setLoadingSubmit] = useState(false);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [filterSearch, setFilterSearch] = useState("");
    const [statusTab, setStatusTab] = useState("P"); 
    const [actionAnchorEl, setActionAnchorEl] = useState(null);
    const [selectedStudent, setSelectedStudent] = useState(null);
    const [updateDialog, setUpdateDialog] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

    // --- API Calls ---
    const getMissingStudentList = useCallback(async () => {
        setLoading(true);
        try {
            const response = await fetch(`https://www.geniusmileniaeducation.link/db-api/exam-missing-student`);
            const resJson = await response.json();
            if (resJson.status === 1) {
                setMissingStudentList(resJson.data || []);
            } else {
                showSnackbar(resJson.message, "error");
            }
        } catch (error) {
            showSnackbar("Failed to connect to server", "error");
        } finally {
            setLoading(false);
        }
    }, []);

    const getStudentList = async () => {
        setLoadingInfo(true);
        try {
            const response = await fetch(`https://www.geniusmileniaeducation.link/db-api/students`);
            const resJson = await response.json();
            if (resJson.status === 1) {
                setStudentList(resJson.data || []);
            } else {
                showSnackbar(resJson.message, "error");
            }
        } catch (error) {
            showSnackbar("Connection Error", "error");
        } finally {
            setLoadingInfo(false);
        }
    };

    const handleSubmitStudent = async () => {
        if (!studentInfo) return showSnackbar("Please select a student", "warning");

        setLoadingSubmit(true);
        try {
            const res = await fetch(`https://www.geniusmileniaeducation.link/db-api/exams/insert-missing-student`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    missingExamId: selectedStudent?.missingId,
                    studentName: studentInfo.fullname, // Official name from directory
                    marks: selectedStudent?.marks,
                    examId: selectedStudent?.examId,
                    studentId: studentInfo.studentId
                })
            });
            const json = await res.json();
            if (json.status === 1) {
                showSnackbar("Student record linked successfully", "success");
                setUpdateDialog(false);
                getMissingStudentList(); 
            } else {
                showSnackbar(json.message, "error");
            }
        } catch (err) {
            showSnackbar("Connection Error", "error");
        } finally {
            setLoadingSubmit(false);
        }
    };

    const showSnackbar = (message, severity = "success") => {
        setSnackbar({ open: true, message, severity });
    };

    useEffect(() => {
        getMissingStudentList();
        localStorage.setItem("current_tab", "Exam Missing Student");
    }, [getMissingStudentList]);

    const handleActionOpen = (event, student) => {
        setActionAnchorEl(event.currentTarget);
        setSelectedStudent(student);
    };

    const handleActionClose = () => {
        setActionAnchorEl(null);
    };

    const filteredData = useMemo(() => {
        const searchStr = filterSearch.toUpperCase();
        return missingStudentList.filter(item => {
            const matchesStatus = item.status === statusTab;
            const matchesSearch = (item.studentName?.toUpperCase().includes(searchStr)) ||
                                 (item.examName?.toUpperCase().includes(searchStr));
            return matchesStatus && matchesSearch;
        });
    }, [missingStudentList, filterSearch, statusTab]);

    const paginatedData = useMemo(() => {
        return filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
    }, [filteredData, page, rowsPerPage]);

    return (
        <Box sx={{ display: 'flex', bgcolor: colors.sidebarBg, minHeight: '100vh' }}>
            <CssBaseline />

            <Box component="main" sx={{ flexGrow: 1, p: 3, bgcolor: colors.white, m: 2, borderRadius: 5 }}>
                
                <Breadcrumbs sx={{ mb: 2 }}>
                    <Link underline="hover" color="inherit" onClick={() => navigate("/Portal")} sx={{ cursor: 'pointer' }}>
                        Portal
                    </Link>
                    <Typography color="text.primary">Exam Missing Student</Typography>
                </Breadcrumbs>

                <Header title="Missing Student Records" subtitle="Manage missing student in examination" />

                <Paper elevation={0} sx={{ p: 2, mb: 3, bgcolor: colors.filterBg, borderRadius: 3 }}>
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center" justifyContent="space-between">
                        <Tabs 
                            value={statusTab} 
                            onChange={(e, val) => { setStatusTab(val); setPage(0); }}
                            sx={{
                                '& .MuiTabs-indicator': { bgcolor: colors.accentDark },
                                '& .MuiTab-root.Mui-selected': { color: colors.accentDark, fontWeight: 'bold' }
                            }}
                        >
                            <Tab label="Pending Linking" value="P" />
                            <Tab label="Completed" value="C" />
                        </Tabs>

                        <TextField
                            placeholder="Search name or exam..."
                            size="small"
                            value={filterSearch}
                            onChange={(e) => { setFilterSearch(e.target.value); setPage(0); }}
                            sx={{ width: 350, '& .MuiOutlinedInput-root': { borderRadius: 3, bgcolor: '#F8F9FA' } }}
                            InputProps={{ 
                                startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment>
                            }}
                        />
                    </Stack>
                </Paper>

                <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #F0E6CC', borderRadius: 4, maxHeight: '55vh' }}>
                    <Table stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ bgcolor: colors.tableHeader, fontWeight: 'bold', color: colors.textMain }}>Student Name</TableCell>
                                <TableCell sx={{ bgcolor: colors.tableHeader, fontWeight: 'bold', color: colors.textMain }}>Exam Session</TableCell>
                                <TableCell sx={{ bgcolor: colors.tableHeader, fontWeight: 'bold', color: colors.textMain }}>Marks</TableCell>
                                <TableCell sx={{ bgcolor: colors.tableHeader, fontWeight: 'bold', color: colors.textMain }}>Status</TableCell>
                                <TableCell align="right" sx={{ bgcolor: colors.tableHeader, fontWeight: 'bold', color: colors.textMain }}>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                                        <CircularProgress size={30} sx={{ color: colors.accentDark }} />
                                    </TableCell>
                                </TableRow>
                            ) : paginatedData.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={5} align="center" sx={{ py: 3 }}>No Records Found</TableCell>
                                </TableRow>
                            ) : paginatedData.map((row) => (
                                <TableRow key={row.missingId} hover>
                                    <TableCell>
                                        <Stack direction="row" spacing={2} alignItems="center">
                                            <Avatar sx={{ bgcolor: colors.tableHeader, color: colors.textMain, fontSize: 14, fontWeight: 'bold' }}>
                                                {row.studentName?.charAt(0)}
                                            </Avatar>
                                            <Typography variant="subtitle2" fontWeight="bold">{row.studentName}</Typography>
                                        </Stack>
                                    </TableCell>
                                    <TableCell>{row.examName || '-'}</TableCell>
                                    <TableCell>{row.marks || '-'}</TableCell>
                                    <TableCell>
                                        <Chip
                                            label={row.status === "C" ? "Linked" : "Pending"}
                                            size="small"
                                            sx={{
                                                borderRadius: 1.5,
                                                bgcolor: row.status === "C" ? '#E8F5E9' : '#FFF3E0',
                                                color: row.status === "C" ? '#2E7D32' : '#E65100',
                                                fontWeight: 'bold'
                                            }}
                                        />
                                    </TableCell>
                                    <TableCell align="right">
                                        {row.status === "P" && (
                                            <IconButton size="small" onClick={(e) => handleActionOpen(e, row)}><MoreVert /></IconButton>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>

                <TablePagination
                    rowsPerPageOptions={[5, 10, 25]}
                    component="div"
                    count={filteredData.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={(e, newPage) => setPage(newPage)}
                    onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                />

                {/* Action Menu */}
                <Menu anchorEl={actionAnchorEl} open={Boolean(actionAnchorEl)} onClose={handleActionClose}>
                    <MenuItem onClick={() => { 
                        setStudentInfo(null); 
                        setUpdateDialog(true); 
                        getStudentList(); 
                        handleActionClose(); 
                    }}>
                        <Edit fontSize="small" sx={{ mr: 1 }} /> Link Official Record
                    </MenuItem>
                </Menu>

                {/* --- UPDATE DIALOG WITH SEARCHABLE AUTOCOMPLETE --- */}
                <Dialog 
                    open={updateDialog} 
                    onClose={() => !loadingSubmit && setUpdateDialog(false)} 
                    PaperProps={{ sx: { borderRadius: 4, minWidth: 450 } }}
                >
                    <Box p={4} textAlign="center">
                        <DialogTitle sx={{ fontWeight: 'bold', p: 0, mb: 1 }}>Reconcile Student Record</DialogTitle>
                        {loadingInfo ? (
                            <Box sx={{ py: 4 }}><CircularProgress size={30} /></Box>
                        ) : (
                            <>
                                <Typography variant="body2" sx={{ mb: 4, color: 'gray' }}>
                                    Linking exam result for <strong>{selectedStudent?.studentName}</strong> <br/> to an system profile in the directory.
                                </Typography>
                                
                                <Autocomplete
                                    options={studentList}
                                    getOptionLabel={(option) => option.fullname || ""}
                                    value={studentInfo}
                                    onChange={(event, newValue) => setStudentInfo(newValue)}
                                    renderInput={(params) => (
                                        <TextField 
                                            {...params} 
                                            label="Search System Directory..." 
                                            variant="outlined" 
                                            size="small"
                                            sx={{ mb: 4, textAlign: 'left' }}
                                        />
                                    )}
                                    sx={{ width: '100%' }}
                                />

                                <Stack direction="row" spacing={2} justifyContent="center">
                                    <Button 
                                        onClick={() => setUpdateDialog(false)} 
                                        color="inherit" 
                                        variant="outlined" 
                                        disabled={loadingSubmit}
                                        sx={{ borderRadius: 2, textTransform: 'none', px: 4 }}
                                    >
                                        Cancel
                                    </Button>
                                    <Button
                                        onClick={handleSubmitStudent}
                                        variant="contained"
                                        disabled={loadingSubmit || !studentInfo}
                                        sx={{ 
                                            borderRadius: 2, 
                                            textTransform: 'none', 
                                            color: 'white',
                                            px: 4,
                                            '&:hover': { bgcolor: '#e68a00'  } 
                                        }}
                                    >
                                        {loadingSubmit ? <CircularProgress size={20} color="inherit" /> : "Complete Linking"}
                                    </Button>
                                </Stack>
                            </>
                        )}
                    </Box>
                </Dialog>

                <Snackbar
                    open={snackbar.open}
                    autoHideDuration={4000}
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                >
                    <Alert severity={snackbar.severity} variant="filled" sx={{ borderRadius: 2 }}>
                        {snackbar.message}
                    </Alert>
                </Snackbar>
            </Box>
        </Box>
    );
}